import $ from 'jquery';

import { hasIntersectionObserver } from 'chairisher/view/helper/intersectionobserver';

/**
 * A component that turns an element into a Scroller, which cycles through the elements
 * in either direction until the end is hit.
 *
 * @param {Object} settings The object containing the options for the scroller component
 * @param {Element} settings.container The outermost element that scrolls
 * @param {Array.<Element>} settings.children Collection of child elements displayed in the scroller
 */
class Scroller {
    constructor(settings = {}) {
        this.containerEl = settings.container ?? null;
        this.containerBodyEl = settings.containerBody ?? null;
        this.children = settings.children ?? null;

        this.$containerEl = this.containerEl ? $(this.containerEl) : null;
        this.$containerBodyEl = this.containerBodyEl ? $(this.containerBodyEl) : null;
        this.$windowEl = $(window);

        this.$controllerLeftEl = null;
        this.$controllerRightEl = null;
        this.childData = null;
        this.currentIndex = 0;
        this.eventNameSpace = 'scroller';
        this.isInitialized = false;
        this.resizeHandle = null;
        this.isAnimatedScroll = false;

        this.$windowEl.smartresize(() => this.resizeAction(), this.nameSpacedEvent('resize'));

        this.initializeChildren(settings.children);

        this.buildControls();

        this.isInitialized = true;
    }

    /**
     * Static factory method to create a new Scroller instance.
     *
     * @param {Object} settings - Configuration options for the Scroller.
     * @returns {Scroller} A new Scroller instance.
     */
    static create(settings) {
        return new Scroller(settings);
    }

    /**
     * Scrolls the container backward by one element index
     */
    scrollBackward() {
        const previousChild = this.getChildByIndex(this.currentIndex - 1);
        if (previousChild && !this.isScrolling) {
            this.currentIndex -= 1;
            this.scrollContainer(this.getChildScrollLeftByIndex(this.currentIndex));
        }
    }

    /**
     * Scrolls the container forward by one element index
     */
    scrollForward() {
        const nextChild = this.getChildByIndex(this.currentIndex + 1);
        if (nextChild && !this.isScrolling) {
            this.currentIndex += 1;
            this.scrollContainer(this.getChildScrollLeftByIndex(this.currentIndex));
        }
    }

    /**
     * Builds clickable left and right controls that scroll the container forward and backward
     * Upon manual scroll events, the arrow visibility is updated
     */
    buildControls() {
        this.$controllerLeftEl = $('<span></span>', {
            class: 'cicon cicon-chevron-left js-scroll-left',
        });

        this.$controllerRightEl = $('<span></span>', {
            class: 'cicon cicon-chevron-right js-scroll-right',
        });

        this.updateControlElementVisibility();

        this.$containerEl.append([this.$controllerLeftEl, this.$controllerRightEl]);

        const click = this.nameSpacedEvent('click');
        this.$controllerLeftEl.on(click, () => this.scrollBackward());
        this.$controllerRightEl.on(click, () => this.scrollForward());

        // Update arrow visibility when scrolling finishes
        this.$containerBodyEl.smartscrollend(() => {
            if (!this.isAnimatedScroll) {
                // ignore scrolls triggered by jQuery animate() after the first one
                this.updateCurrentIndexByContainerPosition();
                this.updateControlElementVisibility();
            }
        }, this.eventNameSpace);
    }

    /**
     * Initializes children passed in to be used with the scroller and kicks everything off
     *
     * @param {Array.<Element>} children The children to initialize
     */
    initializeChildren(children) {
        this.childData = {};

        if (hasIntersectionObserver()) {
            const intersectionObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach((entry) => {
                    const { target } = entry;
                    const index = this.children.indexOf(target);

                    this.childData[index] = entry.boundingClientRect;
                    observer.unobserve(target);
                });
                this.resizeAction(); // kick everything off
            });

            for (let i = 0; i < children.length; i += 1) {
                intersectionObserver.observe(children[i]);
            }
        }
    }

    /**
     * @param {string} eventName The name of the event to namespace
     * @returns {string} The namespaced event
     */
    nameSpacedEvent(eventName) {
        return [eventName, this.eventNameSpace].join('.');
    }

    /**
     * Recalculates all of the necessary dimensions for the given screen size and re-positions everything
     */
    resizeAction() {
        if (this.resizeHandle) {
            window.clearTimeout(this.resizeHandle);
        }

        this.resizeHandle = window.setTimeout(() => {
            this.syncChildData();

            // Scroll the current child to the left of the viewport in case it was "pushed" by a resize
            this.scrollContainer(this.getChildScrollLeftByIndex(this.currentIndex), true);

            // Update arrow visibility after resizing
            this.updateControlElementVisibility();
        });
    }

    /**
     * Scrolls the container by a given amount (in pixels)
     *
     * @param {number} scrollAmountInPixels the amount to scroll the container element (in pixels)
     * @param {boolean=} shouldForce whether to force the animation to run
     */
    scrollContainer(scrollAmountInPixels, shouldForce) {
        if (!this.isScrolling || shouldForce) {
            this.isScrolling = true;
            this.isAnimatedScroll = true;

            this.$containerBodyEl.stop().animate(
                {
                    scrollLeft: scrollAmountInPixels,
                },
                () => {
                    this.isScrolling = false;
                    this.isAnimatedScroll = false;
                },
            );
        }
    }

    /**
     * Updates the visibility of the left and right navigation elements.
     */
    updateControlElementVisibility() {
        this.$controllerLeftEl.toggleClass('hidden', this.currentIndex === 0);

        // Now check if the rightmost element is in view
        const lastIndex = this.children.length - 1;
        // Only proceed if childData has been loaded for the last child
        if (this.childData && this.childData[lastIndex]) {
            const bodyScrollLeft = this.$containerBodyEl.scrollLeft();
            const bodyWidth = this.$containerBodyEl.width();
            const lastChildScrollLeft = this.getChildScrollLeftByIndex(this.children.length - 1);
            const isLastChildInViewport = lastChildScrollLeft < bodyScrollLeft + bodyWidth;

            this.$controllerRightEl.toggleClass('hidden', isLastChildInViewport);
        }
    }

    /**
     * Returns an child Element by index
     * @param {number} index
     * @returns {Element|undefined} The child element, if any, at the given index
     */
    getChildByIndex(index) {
        return this.children[index];
    }

    /**
     * Determines the scrollLeft value a child should have based on child index
     *
     * @param {number} index
     * @returns {number} The scrollLeft value a child should have based on its index
     */
    getChildScrollLeftByIndex(index) {
        let scrollLeft = 0;
        for (let i = 0; i < index; i += 1) {
            scrollLeft += this.childData[i].width;
        }
        return scrollLeft;
    }

    /**
     * Updates the current index based on the scrollLeft value of the container
     */
    updateCurrentIndexByContainerPosition() {
        const scrollLeft = this.$containerBodyEl.scrollLeft();
        let runningWidths = 0;

        for (let i = 0; i < this.children.length; i += 1) {
            if (runningWidths + this.childData[i].width <= scrollLeft) {
                runningWidths += this.childData[i].width;
            } else {
                this.currentIndex = i;
                break;
            }
        }
    }

    /**
     * Updates the current childData. Used after a screen resize.
     */
    syncChildData() {
        for (let i = 0; i < this.children.length; i += 1) {
            this.childData[i] = this.children[i].getBoundingClientRect();
        }
    }
}

export default Scroller;
